import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { withTracking } from 'components/tracking';
import withApplicationConfig from 'containers/withApplicationConfig';
import { withLoginLandingPage } from 'containers/withQueries';
import {
  Loading as LoadingPage, sizes, CtaBanner,
  HtmlParser, CampaignLayoutC, CagTypography, Button, WithFixedBannerLayout,
  CagTypographyVariant, cagColors, CagButtonVariant,
} from '@hyphengroup/cag-components';
import Layout from 'components/layout';
import {
  getStaticText,
  getImages,
  getObjectFromArrayByKey,
  showStaticTextIfSpecified,
} from 'helpers/Utils';
import { path } from 'ramda';
import { CustomerProfileContext } from 'containers/withCustomerProfileContext';
import {
  incentivisedLoginCampaignPageLoadEvent,
  incentivisedLoginTopSignupBtnClickEvent,
  incentivisedLoginTopSigninLinkClickEvent,
  incentivisedLoginSignupLoginTextClickEvent,
  incentivisedLoginFooterSignupBtnClickEvent,
  incentivisedLoginSignupBannerClickEvent,
} from 'components/tracking/ILTrackingUtils';

const TermsAndConditionContainer = styled.div`
  a {
    color: ${cagColors.all['text-01']}
  }
`;

const PAGE_VARIANTS = {
  BLUE: 'blue',
  GRAD_01: 'grad01',
};

const mapVariantToSectionVariant = {
  insuranceBanner: {
    [PAGE_VARIANTS.BLUE]: 'bkground-01',
    [PAGE_VARIANTS.GRAD_01]: 'grad-01',
  },
  jumbotronCTAButton: {
    [PAGE_VARIANTS.BLUE]: CagButtonVariant.PRIMARY_CTA,
    [PAGE_VARIANTS.GRAD_01]: CagButtonVariant.SECONDARY_CTA,
  },
};

export function LoginLanding({
  loginLandingQuery,
  track,
  variant,
  hasStandaloneLoginPage = false,
}) {
  const [bannerHeight, setBannerHeight] = useState();
  const bannerRef = useRef(null);

  useEffect(() => {
    incentivisedLoginCampaignPageLoadEvent(track);
  }, []);

  useEffect(() => {
    if (bannerRef?.current) {
      const { height } = bannerRef.current.getBoundingClientRect();
      setBannerHeight(height);
    }
  }, [bannerRef]);

  if (!loginLandingQuery || loginLandingQuery.loading) {
    return <LoadingPage />;
  }

  const {
    pageConfig,
    componentConfig,
    customerProfile,
    refetch,
    coverageInfo,
    termsAndConditions,
    seoMetadata,
  } = loginLandingQuery;
  const staticText = getStaticText(loginLandingQuery);
  const images = getImages(loginLandingQuery);
  const landingSections = getObjectFromArrayByKey(path(['landingSections'], loginLandingQuery), 'title');

  const redirectToLoginPage = () => {
    const redirectUri = encodeURIComponent(`${window.location.origin}/${staticText.DASHBOARD_ROUTE_NAME}`);
    window.location.href = `/login?redirect_uri=${redirectUri}`;
  };

  const getTopJumbotron = () => ({
    variant,
    heading1: staticText.INCENTIVISED_LANDING_TITLE,
    heading2: staticText.INCENTIVISED_LANDING_SUBTITLE,
    buttonText: staticText.INCENTIVISED_LANDING_TOP_CTA_LABEL,
    buttonVariant: mapVariantToSectionVariant.jumbotronCTAButton[variant],
    ctaButton: {
      text: staticText.INCENTIVISED_LANDING_TOP_CTA_LABEL,
      onClick: () => {
        incentivisedLoginTopSignupBtnClickEvent(track);
        redirectToLoginPage();
      },
    },
    backgroundImage: variant === PAGE_VARIANTS.GRAD_01 && [
      `url(${images?.INCENTIVISED_LANDING_HERO_MOBILE?.image?.url || ''})`,
      `url(${images?.INCENTIVISED_LANDING_HERO_TABLET?.image?.url || ''})`,
      `url(${images?.INCENTIVISED_LANDING_HERO_DESKTOP?.image?.url || ''})`,
    ],
    image: variant === PAGE_VARIANTS.BLUE && images?.INCENTIVISED_LANDING_HERO?.image?.url,
    guideTextContent: (
      <>
        {staticText.INCENTIVISED_LANDING_ALREADY_SIGNED_UP_PRE}
        &nbsp;
        <Button
          dataSelectorId="alreadySignedUpCta"
          variant="secondaryLink-bold"
          onClick={() => {
            incentivisedLoginTopSigninLinkClickEvent(track);
            redirectToLoginPage();
          }}
        >
          {staticText.INCENTIVISED_LANDING_ALREADY_SIGNED_UP_CTA_LABEL}
        </Button>
        &nbsp;
        {staticText.INCENTIVISED_LANDING_ALREADY_SIGNED_UP_POST}
      </>),
  });

  const insuranceFeatureList = {
    features: landingSections?.INCENTIVISED_LANDING_SECTION?.section?.map(section => ({
      text: section.description,
      image: section?.image?.url,
      imageSize: {
        width: [sizes(7), sizes(7), sizes(7), sizes(14)],
      },
      baseTitle: 'primary',
    })) || [],
    title: landingSections?.INCENTIVISED_LANDING_SECTION?.heading,
    titleProps: {
      as: 'div',
      variant: 'secondary',
      sizeFont: 'px32',
      weight: 'normal',
      align: ['left', 'left', 'center'],
      marginBottom: sizes(4),
    },
  };

  const getInsuranceBanner = ({ renderHotLinks }) => ({
    signUpText: renderHotLinks ? (
      <>
        {staticText.INCENTIVISED_LANDING_SECTION_PRE_TEXT}
        &nbsp;
        <Button
          variant="link-inherit"
          display="inline"
          onClick={() => {
            incentivisedLoginSignupLoginTextClickEvent({ track, componentType: 'text' });
            redirectToLoginPage();
          }}
        >
          <CagTypography
            variant={{ _: CagTypographyVariant.H2, 2: CagTypographyVariant.H1 }}
            color="brand-01"
            as="div"
          >
            <u>{staticText.INCENTIVISED_LANDING_SECTION_SIGN_UP}</u>
          </CagTypography>
        </Button>
        &nbsp;
        {staticText.INCENTIVISED_LANDING_SECTION_OR_JOINER}
        &nbsp;
        <Button
          variant="link-inherit"
          display="inline"
          onClick={() => {
            incentivisedLoginSignupLoginTextClickEvent({ track, componentType: 'text' });
            redirectToLoginPage();
          }}
        >
          <CagTypography
            variant={{ _: CagTypographyVariant.H2, 2: CagTypographyVariant.H1 }}
            color="brand-01"
            as="div"
          >
            <u>{staticText.INCENTIVISED_LANDING_SECTION_SIGN_IN}</u>
          </CagTypography>
        </Button>
        &nbsp;
        {staticText.INCENTIVISED_LANDING_SECTION_POST_TEXT}
      </>
    )
      : staticText.INCENTIVISED_LANDING_SECTION_BUTTON_PRE_TEXT,
    title: staticText.INCENTIVISED_LANDING_WHAT_COVER,
    image: {
      title: images?.INCENTIVISED_LANDING_SHIELD?.image?.title,
      url: images?.INCENTIVISED_LANDING_SHIELD?.image?.url,
    },
    description: staticText.INCENTIVISED_LANDING_COVERAGE_INFO,
    receiveText: staticText.INCENTIVISED_LANDING_COVERAGE_INFO_HEADING,
    receiveList: coverageInfo[0].text,
    variant: mapVariantToSectionVariant.insuranceBanner[variant],
    ctaButton: !renderHotLinks && {
      text: staticText.INCENTIVISED_LANDING_SECTION_AUTH,
      onClick: () => {
        redirectToLoginPage();
        incentivisedLoginSignupLoginTextClickEvent({ track, componentType: 'button' });
      },
    },
  });

  const newFeatures = {
    title: staticText.INCENTIVISED_LANDING_FEATURES_TITLE,
    description: staticText.INCENTIVISED_LANDING_FEATURES_SUBTITLE,
    image: {
      title: images?.INCENTIVISED_LANDING_FEATURES?.image?.title,
      url: images?.INCENTIVISED_LANDING_FEATURES?.image?.url,
    },

    featureList: [
      {
        title: staticText.INCENTIVISED_LANDING_OFFERS_TITLE,
        text: staticText.INCENTIVISED_LANDING_OFFERS_TEXT,
      },
      {
        title: staticText.INCENTIVISED_LANDING_PORTFFOLIO_TITLE,
        text: staticText.INCENTIVISED_LANDING_PORTFOLIO_TEXT,
      },
      {
        title: staticText.INCENTIVISED_LANDING_HEALTHCHECK_TITLE,
        text: staticText.INCENTIVISED_LANDING_HEALTHCHECK_TEXT,
      },
    ],
  };

  const getBottomJumbotron = () => ({
    heading1: staticText.INCENTIVISED_LANDING_FINAL_CTA_TITLE,
    heading2: staticText.INCENTIVISED_LANDING_FINAL_CTA_SUBTITLE,
    ctaButton: {
      text: staticText.INCENTIVISED_LANDING_FINAL_CTA_LABEL,
      onClick: () => {
        incentivisedLoginFooterSignupBtnClickEvent(track);
        redirectToLoginPage();
      },
    },
  });

  const termsAndConditionsText = termsAndConditions?.length && termsAndConditions[0].text;
  let termsAndConditionsSection;

  if (termsAndConditionsText.length) {
    const termsAndConditionsText2 = termsAndConditionsText[2]?.split('{termsAndConditions}');

    termsAndConditionsSection = (
      <TermsAndConditionContainer>
        <HtmlParser>
          {termsAndConditionsText[0]}
          <br />
          <br />
          <HtmlParser>
            {termsAndConditionsText[1]}
          </HtmlParser>
          <HtmlParser>
            {termsAndConditionsText2 && termsAndConditionsText2[0]}
          </HtmlParser>
        </HtmlParser>
        <a
          href={staticText.INCENTIVISED_LANDING_TERMS_AND_CONDITIONS_LINK}
          rel="noopener noreferrer"
          target="_blank"
        >
          <CagTypography
            variant={CagTypographyVariant.BODY_SML}
            color="text-01"
          >
            <u>{staticText.INCENTIVISED_LANDING_TERMS_AND_CONDITIONS}</u>
          </CagTypography>
        </a>
        {termsAndConditionsText2
        && (
        <CagTypography
          variant={CagTypographyVariant.BODY_SML}
          color="text-01"
        >
          <HtmlParser>
            {termsAndConditionsText2[1]}
          </HtmlParser>
        </CagTypography>
        )
      }
      </TermsAndConditionContainer>
    );
  }

  // eslint-disable-next-line react/prop-types
  const getCampaignLayoutC = ({ renderHotLinks }) => (
    <CampaignLayoutC
      topJumbotron={getTopJumbotron()}
      insuranceFeatureList={insuranceFeatureList}
      insuranceBanner={getInsuranceBanner({
        renderHotLinks,
      })}
      newFeatures={newFeatures}
      bottomJumbotron={getBottomJumbotron()}
      termsAndConditions={termsAndConditionsSection}
    />
  );
  return (
    <Layout
      style={{ paddingBottom: bannerHeight && `${bannerHeight}px` }}
      seoMetadata={seoMetadata}
      {...pageConfig}
      customerProfile={customerProfile}
      componentConfig={componentConfig}
      staticText={{
        ...staticText,
        LOGIN_HEADER_SUBTEXT: showStaticTextIfSpecified('INCENTIVISED_LOGIN_HEADER_SUBTEXT', staticText),
      }}
      images={images}
      refetch={refetch}
      hasStandaloneLoginPage={hasStandaloneLoginPage}
    >
      <CustomerProfileContext.Consumer>
        {() => (
          <WithFixedBannerLayout
            bannerRef={bannerRef}
            banner={(
              <CtaBanner
                title={staticText.INCENTIVISED_LANDING_SECTION_BANNER_TITLE}
                ctaButton={{
                  text: [
                    staticText.INCENTIVISED_LANDING_SECTION_BANNER_SIGN_UP,
                    staticText.INCENTIVISED_LANDING_SECTION_BANNER_AUTH,
                  ],
                  onClick: () => {
                    redirectToLoginPage();
                    incentivisedLoginSignupBannerClickEvent(track);
                  },
                }}
              />
            )}
          >
            {getCampaignLayoutC({ renderHotLinks: true })}
          </WithFixedBannerLayout>
        )}
      </CustomerProfileContext.Consumer>
    </Layout>
  );
}

LoginLanding.propTypes = {
  track: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['blue', 'grad01']),
  loginLandingQuery: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    refetch: PropTypes.func.isRequired,
    pageConfig: PropTypes.shape({}).isRequired,
    customerProfile: PropTypes.shape({
      loginState: PropTypes.string.isRequired,
    }).isRequired,
    coverageInfo: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.arrayOf(PropTypes.string).isRequired,
    })).isRequired,
    termsAndConditions: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.arrayOf(PropTypes.string).isRequired,
    })).isRequired,
    componentConfig: PropTypes.shape({}),
    seoMetadata: PropTypes.shape({}),
  }),
  hasStandaloneLoginPage: PropTypes.bool,
};

export default withTracking(
  withApplicationConfig(withLoginLandingPage(LoginLanding), ['languages', 'contentstackKeys'])
);
